'use client';

import type { FC } from 'react';
import { useState } from 'react';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import translations from './AuctionsScreen.translations';
import { ScreenContent, ScreenFixedTop } from '../../screen';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { ErrorMessage } from '../../components/ErrorMessage';
import { EmptyState, TabFilter } from '../../components/ui';
import type { TabFilterTab } from '../../components/ui';
import { AuctionRibbonCard } from '../../components/AuctionRibbonCard';
import { useNavigation } from '../../contexts/NavigationContext';
import type { ActiveAuctionsResponse } from '../../services/api';
import type { ScreenParams } from '../../types/navigation';
import { useAuctionsData } from './useAuctionsData';
import { useAuctionTimer } from './useAuctionTimer';
import type { AuctionStatusFilter } from './types';

interface AuctionsScreenProps {
  params?: ScreenParams;
}

export const AuctionsScreen: FC<AuctionsScreenProps> = ({ params }) => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const [filter, setFilter] = useState<AuctionStatusFilter>(
    (params?.filter as AuctionStatusFilter) || 'all'
  );

  const { auctions, loading, error, loadAuctions } = useAuctionsData(filter);
  // Reloads the list when any auction expires; cards run their own 1s ticker.
  useAuctionTimer({ auctions, onExpired: loadAuctions });

  const auctionTabs: TabFilterTab<AuctionStatusFilter>[] = [
    { id: 'all', label: t('filtersAll') },
    { id: 'ACTIVE', label: t('filtersActive') },
    { id: 'PENDING', label: t('filtersPending') },
    { id: 'COMPLETED', label: t('filtersCompleted') },
  ];

  const handleFilterChange = (newFilter: AuctionStatusFilter) => {
    setFilter(newFilter);
  };

  const handleAuctionClick = (auction: ActiveAuctionsResponse['auctions'][0]) => {
    navigation.navigate('auction-details', { auctionId: auction.id });
  };

  return (
    <>
      <ScreenFixedTop title={t('title')}>
        <TabFilter tabs={auctionTabs} activeTab={filter} onChange={handleFilterChange} />
      </ScreenFixedTop>
      <ScreenContent className="flex-1">
        {loading ? (
          <div className="flex justify-center items-center min-h-[200px] text-white">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <ErrorMessage error={error} message={error} onRetry={loadAuctions} />
        ) : auctions.length === 0 ? (
          <EmptyState message={t('emptyTitle')} />
        ) : (
          <div className="flex flex-col gap-3 p-4">
            {auctions.map((auction) => (
              <AuctionRibbonCard
                key={auction.id}
                auction={auction}
                onClick={() => handleAuctionClick(auction)}
              />
            ))}
          </div>
        )}
      </ScreenContent>
    </>
  );
};
